import {
  IWorkflowStep,
  SitePermitStatus,
  WorkflowStepType,
} from '~/client/graph'
import formStatusesByStepsMap from '~/client/src/shared/constants/formStatusesByStepsMap'
import { getWorkflowStepLevel } from '~/client/src/shared/constants/formStatusesTags'
import { WorkflowStepCategory } from '~/client/src/shared/enums/WorkflowStepCategory'

// localization: no text to translate

export interface IStatusOptionsParams {
  currentStatus: SitePermitStatus
  currentWorkflowStepLevel: number
  workflowSteps: IWorkflowStep[]
}

export interface IStatusItem {
  key: string
  status: SitePermitStatus
  workflowStepLevel: number
  workflowStepId: string
  isSelected: boolean
}

export interface IStatusGroup {
  category: WorkflowStepCategory
  items: IStatusItem[]
}

const statusesByCategoryMap: Record<WorkflowStepCategory, SitePermitStatus[]> =
  {
    [WorkflowStepCategory.INITIATION]: [
      SitePermitStatus.Requested,
      SitePermitStatus.Submitted,
      SitePermitStatus.Changed,
    ],
    [WorkflowStepCategory.APPROVAL]: [
      SitePermitStatus.Reviewed,
      SitePermitStatus.Accepted,
      SitePermitStatus.Denied,
    ],
    [WorkflowStepCategory.START]: [
      SitePermitStatus.Active,
      SitePermitStatus.OnSite,
    ],
    [WorkflowStepCategory.INSPECTION]: [
      SitePermitStatus.ToInspect,
      SitePermitStatus.Passed,
      SitePermitStatus.Failed,
    ],
    [WorkflowStepCategory.FINISH]: [SitePermitStatus.Done],
  }

function getStatusCategory(status: SitePermitStatus): WorkflowStepCategory {
  return Object.values(WorkflowStepCategory).find(category =>
    statusesByCategoryMap[category]?.includes(status),
  )
}

function getStatusItems({
  workflowSteps,
  currentStatus,
  currentWorkflowStepLevel,
}: IStatusOptionsParams): IStatusItem[] {
  return workflowSteps.flatMap((step, idx) => {
    const isLastStep = workflowSteps.length - 1 === idx
    const statuses = isLastStep
      ? step.type === WorkflowStepType.Inspection
        ? [SitePermitStatus.Failed, SitePermitStatus.Done]
        : [SitePermitStatus.Done]
      : formStatusesByStepsMap[step.type]
    const stepLevel = getWorkflowStepLevel(step.id, workflowSteps)

    return statuses.map(status => {
      const isTerminalNegative =
        isLastStep &&
        (status === SitePermitStatus.Failed ||
          status === SitePermitStatus.Denied)
      const workflowStepLevel = isTerminalNegative ? 0 : stepLevel

      return {
        key: step.id + status,
        status,
        workflowStepLevel,
        workflowStepId: step.id,
        isSelected:
          currentStatus === status &&
          currentWorkflowStepLevel === workflowStepLevel,
      }
    })
  })
}

// Groups are consecutive runs, not unique categories, so a category that
// recurs in a later step keeps its own labelled section, as it did before.
export function getStatusGroups(props: IStatusOptionsParams): IStatusGroup[] {
  return getStatusItems(props).reduce<IStatusGroup[]>((groups, item) => {
    const category = getStatusCategory(item.status)
    const lastGroup = groups[groups.length - 1]

    if (lastGroup?.category === category) {
      lastGroup.items.push(item)
    } else {
      groups.push({ category, items: [item] })
    }

    return groups
  }, [])
}
